import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, InjectionToken, inject, signal } from '@angular/core';
import { Observable, Subject, merge, race, throwError, timer } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';

/** Error code of the API while the database is paused or resuming (HTTP 503). */
export const DATABASE_STARTING = 'DATABASE_STARTING';

export interface DatabaseWakeupConfig {
  /** Pause between two attempts of a waiting request. */
  retryDelayMs: number;
  /** How long requests wait in total before the notice shows an error. */
  maxWaitMs: number;
}

export const DATABASE_WAKEUP_CONFIG = new InjectionToken<DatabaseWakeupConfig>(
  'DATABASE_WAKEUP_CONFIG',
  { factory: () => ({ retryDelayMs: 5000, maxWaitMs: 150000 }) }
);

/** Whether the API answered that the database is not available yet. */
export const isDatabaseStarting = (error: unknown): boolean => {
  if (!(error instanceof HttpErrorResponse) || error.status !== 503) {
    return false;
  }
  let body: unknown = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return false;
    }
  }
  return (
    !!body &&
    typeof body === 'object' &&
    (body as { error?: unknown }).error === DATABASE_STARTING
  );
};

/**
 * `waiting`: requests wait for the database and are repeated; `failed`: they
 * waited too long and wait for the user to try again or give up.
 */
export type DatabaseWakeupState = 'idle' | 'waiting' | 'failed';

/**
 * The state of the database wake-up, shared by all requests: the dev and int
 * databases pause when idle and need up to about a minute to resume. Requests
 * that get DATABASE_STARTING register here and are repeated (see
 * DatabaseStartingInterceptor); one notice shows the state for all of them.
 */
@Injectable({ providedIn: 'root' })
export class DatabaseWakeupService {
  private config = inject(DATABASE_WAKEUP_CONFIG);

  private readonly stateSignal = signal<DatabaseWakeupState>('idle');
  readonly state = this.stateSignal.asReadonly();

  private readonly waiting = new Set<number>();
  private nextId = 0;
  private deadline = 0;

  /** The database answered: waiting requests try again at once. */
  private readonly awake = new Subject<void>();
  /** The user asked to try again after the time ran out. */
  private readonly resume = new Subject<void>();
  /** The user closed the error: waiting requests fail. */
  private readonly giveUp = new Subject<void>();

  /** A number for one request, to track whether it is waiting. */
  register(): number {
    this.nextId += 1;
    return this.nextId;
  }

  /**
   * A request got DATABASE_STARTING. Emits when it should be sent again, or
   * fails with the error when the user gives up.
   */
  retryAfter(id: number, error: HttpErrorResponse): Observable<unknown> {
    this.waiting.add(id);
    const now = Date.now();
    if (this.stateSignal() === 'idle') {
      this.deadline = now + this.config.maxWaitMs;
      this.stateSignal.set('waiting');
    }
    if (this.stateSignal() === 'waiting' && now >= this.deadline) {
      this.stateSignal.set('failed');
    }
    if (this.stateSignal() === 'failed') {
      return merge(
        this.resume,
        this.giveUp.pipe(mergeMap(() => throwError(() => error)))
      ).pipe(take(1));
    }
    return race(timer(this.config.retryDelayMs), this.awake).pipe(
      take(1),
      map(() => undefined)
    );
  }

  /** A request got an answer: the database is up again. */
  succeeded(id: number): void {
    this.waiting.delete(id);
    if (this.stateSignal() !== 'idle') {
      this.stateSignal.set('idle');
      this.awake.next();
    }
  }

  /** A request ended (answer, other error or cancelled). */
  settled(id: number): void {
    if (this.waiting.delete(id) && this.waiting.size === 0) {
      this.stateSignal.set('idle');
    }
  }

  /** After the time ran out: the waiting requests try again. */
  retryNow(): void {
    if (this.stateSignal() !== 'failed') {
      return;
    }
    this.deadline = Date.now() + this.config.maxWaitMs;
    this.stateSignal.set('waiting');
    this.resume.next();
  }

  /** After the time ran out: the waiting requests fail with their error. */
  dismiss(): void {
    this.stateSignal.set('idle');
    this.waiting.clear();
    this.giveUp.next();
  }
}
